import { Activity, Calendar, ChevronDown, ChevronRight, BarChart3 } from "lucide-react";
import { formatDateToLocaleString } from "@/shared/utils/date-utils";
import { getCurrentMonthDataUsage, getPreviousMonthDataUsage, getCurrentMonthUsagePercentage } from "@/shared/utils/serviceLineCalculations";
import { formatDataUsage } from "@/shared/utils/format-utils";
import { DataUsage, ServiceLine } from "@/shared/types/serviceLineTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { ServiceLineChartDialog } from "@/domains/service-lines/dialogs/ServiceLineChartDialog";
import React from "react";
import { DataConsumptionType, getDataConsumptionTypeChartFill, getDataConsumptionTypeTextColorClass, getUtilizationBGColorClass } from "@/shared/utils/color-utils-data-usages";
import EmptyView from "@/shared/components/EmptyView";
import { Separator } from "@/shared/ui/separator";
import { ServiceLineDailyUsageSection } from "./ServiceLineDailyUsageSection";

export function ServiceLineUsageSection({
    serviceLine,
    dataUsages,
}: {
    serviceLine: ServiceLine,
    dataUsages: DataUsage[] | []
}) {
    const [openSections, setOpenSections] = React.useState<Set<number>>(new Set());
    const [isChartDialogOpen, setIsChartDialogOpen] = React.useState<boolean>(false);

    const toggleSection = (index: number) => {
        const newOpenSections = new Set(openSections);
        if (newOpenSections.has(index)) {
            newOpenSections.delete(index);
        } else {
            newOpenSections.add(index);
        }
        setOpenSections(newOpenSections);
    };

    const handleViewChart = () => {
        setIsChartDialogOpen(true);
    };

    const handleCloseChartDialog = () => {
        setIsChartDialogOpen(false);
    };

    const currentMonthDataUsage = React.useMemo(() => {
        return getCurrentMonthDataUsage(dataUsages);
    }, [dataUsages]);

    const lastMonthUsage = React.useMemo(() => {
        return getPreviousMonthDataUsage(dataUsages);
    }, [dataUsages]);

    const usagePercentage = React.useMemo(() => {
        return getCurrentMonthUsagePercentage(serviceLine);
    }, [serviceLine]);

    return (
        <div className="border rounded-lg">
            <div className="flex flex-row items-center justify-between gap-2 bg-muted p-4 rounded-t-lg">
                <div className="flex items-center gap-2">
                    <Activity className="h-6 w-6" />
                    <h3 className="text-2xl font-bold">Consumption Overview</h3>
                </div>
            </div>
            <div className="p-4">
                {/* Main Usage Stats - Responsive Grid */}
                {dataUsages.length > 0 ? (
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Current Month's Consumption - Enhanced with Progress Bar */}
                            <Card className="relative overflow-hidden">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <Activity className="h-4 w-4" />
                                        Current Month's Consumption
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.CONSUMED)}>{formatDataUsage(currentMonthDataUsage?.totalDataUsageGB || 0)}</span></div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-xs">
                                            <span>Usage</span>
                                            <span>{usagePercentage !== null ? usagePercentage.toFixed(1) + '%' : 'N/A'}</span>
                                        </div>
                                        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden border">
                                            <div
                                                className={`h-full transition-all duration-500 ease-in-out ${getUtilizationBGColorClass(usagePercentage)}`}
                                                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs">This month's consumption</p>
                                </CardContent>
                            </Card>

                            {/* Current Month's Capped Forecast */}
                            <Card className="relative overflow-hidden">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <Activity className="h-4 w-4" />
                                        Capped Forecast *
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.CAPPED_FORECAST)}>{formatDataUsage(currentMonthDataUsage?.cappedForecastGB || 0)}</span></div>
                                    <div className="text-xs leading-relaxed">
                                        * Linear projection based on average daily consumption, capped by plan limit
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Current Month's Uncapped Forecast */}
                            <Card className="relative overflow-hidden">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <Activity className="h-4 w-4" />
                                        Uncapped Forecast *
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.UNCAPPED_FORECAST)}>{formatDataUsage(currentMonthDataUsage?.uncappedForecastGB || 0)}</span></div>
                                    <div className="text-xs leading-relaxed">
                                        * Raw projection without plan constraints
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Previous Month's Consumption */}
                            <Card className="relative overflow-hidden">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                                        <Activity className="h-4 w-4" />
                                        Previous Month's Consumption
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-2">
                                    <div className="text-3xl font-bold"><span className={getDataConsumptionTypeTextColorClass(DataConsumptionType.PREVIOUS_MONTH)}>{formatDataUsage(lastMonthUsage?.totalDataUsageGB || 0)}</span></div>
                                    <div className="text-xs leading-relaxed">
                                        Previous month's total consumption
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        <Separator />
                        {/* Monthly Billing Cycles - Enhanced Layout */}
                        <div className="border rounded-lg">
                            <div className="flex items-center justify-between bg-muted p-4 rounded-t-lg">
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    <h3 className="text-lg font-semibold">Monthly Breakdown</h3>
                                </div>
                                {dataUsages.length > 0 && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleViewChart}
                                        className="gap-2"
                                    >
                                        <BarChart3 className="h-4 w-4" />
                                        Historical Data
                                    </Button>
                                )}
                            </div>
                            <div className="p-4 space-y-4">
                                {dataUsages.slice(0, 2).map((dataUsage, monthIndex) => (
                                    <Card key={monthIndex} className="glass-panel">
                                        <CardHeader className="p-4">
                                            <div className="flex flex-wrap items-center justify-between gap-4">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-4 w-4 text-blue-500" />
                                                    <span className="text-lg font-semibold">
                                                        {formatDateToLocaleString(dataUsage.startDate, { year: 'numeric', month: 'long' })}
                                                    </span>
                                                </div>
                                                <div className="flex flex-wrap items-stretch gap-2">
                                                    <div className="rounded-lg border bg-background p-2">
                                                        <span className="text-xs">Priority Data</span>
                                                        <div
                                                            className="text-lg font-semibold"
                                                            style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.PRIORITY_DATA) }}
                                                        >
                                                            {formatDataUsage(dataUsage.totalDataUsageGB)}
                                                        </div>
                                                    </div>
                                                    <div className="rounded-lg border bg-background p-2">
                                                        <span className="text-xs">Standard Data</span>
                                                        <div
                                                            className="text-lg font-semibold"
                                                            style={{ color: getDataConsumptionTypeChartFill(DataConsumptionType.STANDARD_DATA) }}
                                                        >
                                                            {formatDataUsage(dataUsage.totalStandardGB)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </CardHeader>

                                        {dataUsage.totalDataUsageGB > 0 && (
                                            <CardContent className="px-4 pb-4">
                                                <Collapsible
                                                    open={openSections.has(monthIndex)}
                                                    onOpenChange={() => toggleSection(monthIndex)}
                                                >
                                                    <CollapsibleTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="w-full justify-between h-10"
                                                        >
                                                            <span className="text-sm font-medium text-blue-500">Daily Usage Breakdown</span>
                                                            {openSections.has(monthIndex) ? (
                                                                <ChevronDown className="h-4 w-4 text-blue-500" />
                                                            ) : (
                                                                <ChevronRight className="h-4 w-4 text-blue-500" />
                                                            )}
                                                        </Button>
                                                    </CollapsibleTrigger>

                                                    <CollapsibleContent className="space-y-4 pt-4">
                                                        <ServiceLineDailyUsageSection serviceLineNumber={serviceLine.serviceLineNumber} startDate={dataUsage.startDate} />
                                                    </CollapsibleContent>
                                                </Collapsible>
                                            </CardContent>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <EmptyView
                        icon={Activity}
                        size="medium"
                        title="No Usage Data Available"
                        description="This service line has no usage data available"
                    />
                )}
            </div>

            {/* Service Line Chart Dialog */}
            < ServiceLineChartDialog
                serviceLine={serviceLine}
                isOpen={isChartDialogOpen}
                onClose={handleCloseChartDialog}
            />
        </div >
    );
}